import React, { createContext, useContext, useState, ReactNode } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  toggleSidebar: () => void;
  collapseSidebar: () => void;
  expandSidebar: () => void;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  openMobileSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Check if we're on desktop (lg breakpoint and above)
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

  const toggleSidebar = () => {
    // Only allow toggle on mobile/tablet
    if (!isDesktop) {
      setIsCollapsed(prev => !prev);
    }
  };
  const collapseSidebar = () => {
    // Only allow collapse on mobile/tablet
    if (!isDesktop) {
      setIsCollapsed(true);
    }
  };
  const expandSidebar = () => setIsCollapsed(false);
  
  const toggleMobileSidebar = () => setIsMobileOpen(prev => !prev);
  const closeMobileSidebar = () => setIsMobileOpen(false);
  const openMobileSidebar = () => setIsMobileOpen(true);

  return (
    <SidebarContext.Provider value={{
      isCollapsed,
      isMobileOpen,
      toggleSidebar,
      collapseSidebar,
      expandSidebar,
      toggleMobileSidebar,
      closeMobileSidebar,
      openMobileSidebar
    }}>
      {children}
    </SidebarContext.Provider>
  );
};